<script lang="ts">
import BannerSearch from '@/components/common/BannerSearch.vue';
import { RESOLUTION_TYPE } from '@/stores/data-store';
import { initStore } from '@/stores/store-manager';
import {watch} from 'vue';
import type {PropType} from 'vue';
import { defineComponent, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// content 페이지의 상단 배너
export default defineComponent({
	name: 'MainBanner',
	components: { BannerSearch },
	props: {
		onEnter: {
			type: Function as PropType<(result: string) => void>,
			required: false,
			default: () => ({}),
		},
		modelValue: {
			type: String as PropType<string>,
		},
	},
	setup() {
		const { t } = useI18n({ useScope: 'global' });
		const isTextVisible = ref(false); // 텍스트 가시성 관리
		const isTextChange = ref(false); // 텍스트 스타일(크기) 변화 관리
		const isVideoFailed = ref(false); // 동영상 로딩 실패 시 대체 이미지 표시
		const videoFileUrl = ref<string>('/assets/images/banner/banner_bg.mp4');
		const storeManager = initStore();
    	const isMegaSearchFocus = ref<boolean>(storeManager.stateStore.isMegaSearchFocus);

    watch(
        () => storeManager.stateStore.isMegaSearchFocus,
        () => {
          isMegaSearchFocus.value = storeManager.stateStore.isMegaSearchFocus;
        }
    );

		onMounted(() => {
			if (window.innerWidth <= 425) videoFileUrl.value = '/assets/images/banner/banner_bg_mobile.mp4';
			else if (window.innerWidth <= 1023)
				videoFileUrl.value = '/assets/images/banner/banner_bg_tablet.mp4';

			const video = document.getElementById('background-video') as HTMLVideoElement;

			if (video) {
				video.loop = false; // 루프 재생을 막음 (처음 새로고침 시에만 플레이)

				video.addEventListener('timeupdate', () => {
					if (video.currentTime >= 0.5 && !isTextVisible.value) {
						isTextVisible.value = true; // 동영상 재생 0.5초 후 텍스트 노출
					} else if (video.currentTime >= 5 && !isTextChange.value) {
						isTextChange.value = true; // 동영상 재생 5초 후 텍스트 스타일 변경
					}
				});

				// 동영상 로드 실패 시 대체 이미지 표시
				video.addEventListener('error', () => {
					isVideoFailed.value = true; // 동영상 로드 실패 시 true로 설정
				});

				// 동영상이 로드된 후 자동 재생이 실패할 경우 대체 이미지 표시
				video.addEventListener('canplay', () => {
					video.play().catch(() => {
						isVideoFailed.value = true; // 재생 실패 시 대체 이미지 표시
					});
				});
			}
		});

		return {
			t,
			isTextVisible,
			isTextChange,
			isVideoFailed,
			videoFileUrl,
      		isMegaSearchFocus,
		};
	},
});
</script>

<template>
	<div class="main-banner">
		<div class="video-background">
			<video
				v-if="!isVideoFailed"
				id="background-video"
				autoplay
				muted
				playsinline
				preload="auto"
				controlslist="nodownload nofullscreen noremoteplayback"
				poster="/assets/images/banner/banner_bg.webp">
				<source :src="videoFileUrl" type="video/mp4" />
			</video>
			<img v-else src="/assets/images/banner/banner_bg.webp" alt="Fallback Image" class="fallback-image" />
		</div>
		<div class="main-banner-wrapper">
			<!-- 텍스트는 동영상이 한 번 재생된 후 항상 표시 -->
			<div id="text-overlay" v-if="isTextVisible || isVideoFailed" :class="{ sizeChanged: !isTextChange, colorChanged: isTextVisible }">
				{{ t('layout.contentBanner.mainText') }}
			</div>
			<div class="search-wrapper" :class="{ visible: isTextChange }">
				<!-- <banner-search :model-value="modelValue" @onEnter="onEnter" :read-only="isMegaSearchFocus"/> -->
				<div class="banner-sub-text">{{ t('layout.contentBanner.subText') }}</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.fallback-image {
	width: 100%;
	height: 710px;
	object-fit: cover;
}
</style>
